'use client';
import { useState } from 'react';
import { getWarrantyStatusConfig, formatDate } from '../_helpers';
import { RepairQuoteModal } from './RepairQuoteModal';

/* ─── Color tokens ───────────────────────────────────────────────────────── */
const PRIMARY   = 'var(--primary-color)';
const SECONDARY = 'var(--secondary-color)';
const DIVIDER   = 'rgba(11,49,99,0.1)';

/* ─── Warranty status badge ──────────────────────────────────────────────── */
const WarrantyBadge = ({ status }) => {
    const cfg = getWarrantyStatusConfig(status);
    return (
        <span
            className="inline-flex align-items-center gap-1 font-bold border-round-3xl"
            style={{
                background:    cfg.bg,
                color:         cfg.color,
                border:        `1.5px solid ${cfg.border}`,
                fontSize:      '0.72rem',
                padding:       '0.28rem 0.75rem',
                whiteSpace:    'nowrap',
                letterSpacing: '0.01em',
            }}
        >
            <i className={cfg.icon} style={{ fontSize: '0.68rem' }} />
            {cfg.label}
        </span>
    );
};

/* ─── Quick-view chip ────────────────────────────────────────────────────── */
const Chip = ({ icon, label, value }) => (
    <div
        className="flex flex-column gap-1 border-round-lg p-2"
        style={{
            background: 'rgba(11,49,99,0.05)',
            border:     `1px solid rgba(11,49,99,0.15)`,
            minWidth:   0,
        }}
    >
        <div className="flex align-items-center gap-1">
            <i className={icon} style={{ fontSize: '0.65rem', color: SECONDARY }} />
            <span style={{
                fontSize:      '0.6rem',
                fontWeight:    700,
                color:         SECONDARY,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
            }}>
                {label}
            </span>
        </div>
        <span style={{
            fontSize:   '0.82rem',
            fontWeight: 700,
            color:      PRIMARY,
            wordBreak:  'break-word',
            lineHeight: 1.35,
        }}>
            {value || '—'}
        </span>
    </div>
);

/* ─── Section heading ────────────────────────────────────────────────────── */
const SectionHead = ({ icon, label }) => (
    <div
        className="flex align-items-center gap-2 mb-3"
        style={{
            paddingBottom: '0.5rem',
            borderBottom:  `1px solid ${DIVIDER}`,
        }}
    >
        <div
            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
            style={{ width: 26, height: 26, background: PRIMARY }}
        >
            <i className={icon} style={{ fontSize: '0.62rem', color: '#fff' }} />
        </div>
        <span style={{
            fontSize:      '0.72rem',
            fontWeight:    800,
            color:         PRIMARY,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
        }}>
            {label}
        </span>
    </div>
);

/* ─── Detail row ─────────────────────────────────────────────────────────── */
const DetailRow = ({ icon, label, value }) => (
    <div className="flex align-items-start gap-2">
        <i className={icon} style={{
            fontSize:   '0.8rem',
            color:      SECONDARY,
            marginTop:  '0.22rem',
            flexShrink: 0,
        }} />
        <div style={{ minWidth: 0 }}>
            <p className="m-0" style={{
                fontSize:      '0.6rem',
                fontWeight:    700,
                color:         SECONDARY,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom:  '0.15rem',
            }}>
                {label}
            </p>
            <p className="m-0" style={{
                fontSize:   '0.84rem',
                fontWeight: 600,
                color:      PRIMARY,
                wordBreak:  'break-word',
                whiteSpace: 'pre-line',
                lineHeight: 1.45,
            }}>
                {value || '—'}
            </p>
        </div>
    </div>
);

/* ─── Warranty status banner ─────────────────────────────────────────────── */
const WarrantyBanner = ({ warranty, invoiceDate, onGetQuote }) => {
    const isIn = warranty?.status === 'in_warranty';

    const bannerBg     = isIn ? 'rgba(16,185,129,0.12)'  : 'rgba(239,68,68,0.10)';
    const bannerBorder = isIn ? 'rgba(16,185,129,0.28)'  : 'rgba(239,68,68,0.28)';
    const iconColor    = isIn ? '#34d399'                 : '#f87171';
    const titleColor   = isIn ? '#4ade80'                 : '#f87171';
    const piIcon       = isIn ? 'pi pi-shield'            : 'pi pi-times-circle';
    const titleText    = isIn ? 'Product is under warranty' : 'Warranty has expired';
    const subText      = isIn
        ? `Purchase date: ${formatDate(invoiceDate)}`
        : `Warranty expired · Purchase: ${formatDate(invoiceDate)}`;

    return (
        <div
            className="flex align-items-center justify-content-between gap-3 flex-wrap px-4 py-3"
            style={{
                background:   bannerBg,
                borderTop:    `1px solid ${bannerBorder}`,
                borderBottom: `1px solid ${bannerBorder}`,
            }}
        >
            <div className="flex align-items-center gap-2">
                <div
                    className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                    style={{
                        width:      32,
                        height:     32,
                        background: isIn ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
                        border:     `1.5px solid ${bannerBorder}`,
                    }}
                >
                    <i className={piIcon} style={{ fontSize: '0.85rem', color: iconColor }} />
                </div>
                <div>
                    <p className="m-0 font-bold" style={{ fontSize: '0.88rem', color: titleColor, lineHeight: 1.2 }}>
                        {titleText}
                    </p>
                    <p className="m-0 mt-1" style={{ fontSize: '0.72rem', color: PRIMARY }}>
                        {subText}
                    </p>
                </div>
            </div>

            {isIn && warranty?.warrantyExpiry ? (
                <div
                    className="flex align-items-center gap-2 border-round-lg px-3 py-2"
                    style={{
                        background: 'rgba(52,211,153,0.12)',
                        border:     '1.5px solid rgba(52,211,153,0.3)',
                    }}
                >
                    <i className="pi pi-calendar" style={{ fontSize: '0.78rem', color: '#34d399' }} />
                    <div>
                        <p className="m-0" style={{ fontSize: '0.58rem', fontWeight: 700, color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Expires on
                        </p>
                        <p className="m-0 font-bold" style={{ fontSize: '0.88rem', color: '#4ade80' }}>
                            {formatDate(warranty.warrantyExpiry)}
                        </p>
                    </div>
                </div>
            ) : !isIn ? (
                <button
                    onClick={onGetQuote}
                    className="flex align-items-center gap-2 border-round-lg px-3 py-2"
                    style={{
                        background: 'rgba(248,113,113,0.1)',
                        border:     '1.5px solid rgba(248,113,113,0.3)',
                        cursor:     'pointer',
                        transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.22)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.1)'; }}
                >
                    <i className="pi pi-wrench" style={{ fontSize: '0.78rem', color: '#f87171' }} />
                    <div style={{ textAlign: 'left' }}>
                        <p className="m-0" style={{ fontSize: '0.58rem', fontWeight: 700, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            OOW Repair
                        </p>
                        <p className="m-0" style={{ fontSize: '0.82rem', fontWeight: 700, color: '#f87171' }}>
                            Get a quote →
                        </p>
                    </div>
                </button>
            ) : null}
        </div>
    );
};

/* ─── Main card ──────────────────────────────────────────────────────────── */
export const WarrantyResultCard = ({ item, index, invoiceFile }) => {
    const [expanded,   setExpanded]   = useState(false);
    const [quoteModal, setQuoteModal] = useState(false);

    return (
        <div
            style={{
                borderRadius: '1.1rem',
                border:       `2px solid rgba(11,49,99,0.18)`,
                background:   '#ffffff',
                overflow:     'hidden',
                boxShadow:    '0 4px 20px rgba(11,49,99,0.1)',
            }}
        >
            {/* ── Header ───────────────────────────────────────────────── */}
            <div
                style={{
                    background: PRIMARY,
                    padding:    '1.25rem 1.5rem',
                    position:   'relative',
                    overflow:   'hidden',
                }}
            >
                {/* Card number */}
                <div style={{
                    position:     'absolute', top: '0.75rem', right: '1rem',
                    background:   'rgba(255,255,255,0.2)',
                    border:       '1px solid rgba(255,255,255,0.35)',
                    borderRadius: '999px',
                    padding:      '0.15rem 0.6rem',
                    fontSize:     '0.65rem',
                    color:        '#fff',
                    fontWeight:   700,
                    letterSpacing:'0.04em',
                }}>
                    #{index + 1}
                </div>

                <div className="flex align-items-start gap-3">
                    <div
                        className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                        style={{
                            width:          46, height: 46,
                            background:     'rgba(255,255,255,0.15)',
                            border:         '1.5px solid rgba(255,255,255,0.35)',
                            backdropFilter: 'blur(4px)',
                        }}
                    >
                        <i className="pi pi-box" style={{ fontSize: '1.15rem', color: '#fff' }} />
                    </div>

                    <div className="flex flex-column gap-2 flex-1" style={{ minWidth: 0 }}>
                        <p className="m-0 font-bold" style={{
                            fontSize:     '1rem',
                            color:        '#fff',
                            lineHeight:   1.35,
                            paddingRight: '3rem',
                        }}>
                            {item.product?.name || 'Unknown Product'}
                        </p>

                        <div className="flex align-items-center gap-2 flex-wrap">
                            <WarrantyBadge status={item.warranty?.status} />
                            <span style={{
                                fontSize:     '0.68rem', fontWeight: 700,
                                background:   'rgba(255,255,255,0.22)',
                                color:        '#fff',
                                border:       '1px solid rgba(255,255,255,0.35)',
                                borderRadius: '999px', padding: '0.2rem 0.6rem',
                            }}>
                                Qty: {item.product?.quantity || 1}
                            </span>
                            <span style={{
                                fontSize:     '0.68rem', fontWeight: 700,
                                background:   'rgba(255,255,255,0.22)',
                                color:        '#fff',
                                border:       '1px solid rgba(255,255,255,0.35)',
                                borderRadius: '999px', padding: '0.2rem 0.6rem',
                            }}>
                                ₹{item.product?.totalAmount?.toLocaleString('en-IN') || '—'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Warranty banner ──────────────────────────────────────── */}
            <WarrantyBanner
                warranty={item.warranty}
                invoiceDate={item.invoiceDate}
                onGetQuote={() => setQuoteModal(true)}
            />

            {/* ── Body ─────────────────────────────────────────────────── */}
            <div className="p-4">

                {/* Quick-view chips */}
                <div className="grid mb-0" style={{ margin: '0 -0.4rem 1rem' }}>
                    {[
                        { icon: 'pi pi-hashtag',  label: 'Order No',     value: item.orderNumber },
                        { icon: 'pi pi-calendar', label: 'Order Date',   value: formatDate(item.orderDate?.split(',')[0]) },
                        { icon: 'pi pi-file',     label: 'Invoice No',   value: item.invoiceNumber },
                        { icon: 'pi pi-calendar', label: 'Invoice Date', value: formatDate(item.invoiceDate) },
                    ].map(chip => (
                        <div key={chip.label} className="col-6 sm:col-3" style={{ padding: '0.4rem' }}>
                            <Chip icon={chip.icon} label={chip.label} value={chip.value} />
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: DIVIDER, margin: '0.5rem 0 1.25rem' }} />

                {/* Two-column details */}
                <div className="grid" style={{ margin: 0 }}>
                    <div className="col-12 md:col-6" style={{ paddingRight: '1rem' }}>
                        <SectionHead icon="pi pi-shopping-bag" label="Order Details" />
                        <div className="flex flex-column gap-3">
                            <DetailRow icon="pi pi-hashtag"  label="Order Number"   value={item.orderNumber} />
                            <DetailRow icon="pi pi-calendar" label="Order Date"     value={item.orderDate} />
                            <DetailRow icon="pi pi-file-pdf" label="Invoice Number" value={item.invoiceNumber} />
                            <DetailRow icon="pi pi-calendar" label="Invoice Date"   value={formatDate(item.invoiceDate)} />
                        </div>
                    </div>

                    <div className="col-12 md:col-6" style={{ paddingLeft: '0.5rem' }}>
                        <SectionHead icon="pi pi-box" label="Product Details" />
                        <div className="flex flex-column gap-3">
                            <DetailRow icon="pi pi-tag"            label="HSN Code"     value={item.product?.hsn} />
                            <DetailRow icon="pi pi-sort-amount-up" label="Quantity"     value={`${item.product?.quantity || 1} unit(s)`} />
                            <DetailRow icon="pi pi-wallet"         label="Unit Price"   value={`₹${item.product?.unitPrice?.toLocaleString('en-IN')}`} />
                            <DetailRow icon="pi pi-calculator"     label="Total Amount" value={`₹${item.product?.totalAmount?.toLocaleString('en-IN')}`} />
                        </div>
                    </div>
                </div>

                {/* Expand toggle */}
                <button
                    onClick={() => setExpanded(e => !e)}
                    style={{
                        width:          '100%',
                        background:     expanded ? 'rgba(11,49,99,0.05)' : 'transparent',
                        border:         `1.5px dashed rgba(11,49,99,0.25)`,
                        borderRadius:   '0.7rem',
                        cursor:         'pointer',
                        padding:        '0.6rem 1rem',
                        marginTop:      '1.25rem',
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        gap:            '0.5rem',
                        color:          PRIMARY,
                        fontSize:       '0.8rem',
                        fontWeight:     600,
                        transition:     'all 0.2s',
                        letterSpacing:  '0.01em',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(11,49,99,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = expanded ? 'rgba(11,49,99,0.05)' : 'transparent'; }}
                >
                    <i className="pi pi-building" style={{ fontSize: '0.68rem', color: SECONDARY }} />
                    {expanded ? 'Hide seller & shipping info' : 'Show seller & shipping info'}
                    <i
                        className="pi pi-chevron-down"
                        style={{
                            fontSize:   '0.65rem',
                            color:      SECONDARY,
                            transform:  expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.25s',
                        }}
                    />
                </button>

                {/* Expanded: Seller + Shipping */}
                <div style={{
                    maxHeight:  expanded ? '700px' : '0',
                    overflow:   'hidden',
                    transition: 'max-height 0.4s ease',
                }}>
                    <div
                        className="grid mt-3"
                        style={{ margin: 0, paddingTop: '1rem', borderTop: `1px solid ${DIVIDER}` }}
                    >
                        <div className="col-12 md:col-6" style={{ paddingRight: '1rem' }}>
                            <SectionHead icon="pi pi-building" label="Seller Details" />
                            <div className="flex flex-column gap-3">
                                <DetailRow icon="pi pi-user"       label="Seller Name" value={item.seller?.name} />
                                <DetailRow icon="pi pi-id-card"    label="PAN"         value={item.seller?.pan} />
                                <DetailRow icon="pi pi-verified"   label="GSTIN"       value={item.seller?.gstin} />
                                <DetailRow icon="pi pi-map-marker" label="Address"     value={item.seller?.address} />
                            </div>
                        </div>

                        <div className="col-12 md:col-6" style={{ paddingLeft: '0.5rem' }}>
                            <SectionHead icon="pi pi-truck" label="Shipping Address" />
                            <div className="flex flex-column gap-3">
                                <DetailRow icon="pi pi-user"       label="Recipient"  value={item.shipping?.name} />
                                <DetailRow icon="pi pi-map-marker" label="Address"    value={item.shipping?.address} />
                                <DetailRow icon="pi pi-flag"       label="State Code" value={item.shipping?.stateCode} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Get Repair Quote CTA */}
                <div style={{ height: 1, background: DIVIDER, margin: '1.25rem 0 1rem' }} />
                <button
                    onClick={() => setQuoteModal(true)}
                    style={{
                        width:          '100%',
                        background:     PRIMARY,
                        border:         'none',
                        borderRadius:   '0.8rem',
                        padding:        '0.75rem 1.25rem',
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        gap:            '0.55rem',
                        cursor:         'pointer',
                        color:          '#fff',
                        fontSize:       '0.88rem',
                        fontWeight:     700,
                        transition:     'opacity 0.2s',
                        letterSpacing:  '0.01em',
                        boxShadow:      '0 3px 14px rgba(11,49,99,0.3)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                    <i className="pi pi-wrench" style={{ fontSize: '0.88rem' }} />
                    Get Repair Quote
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.72rem', marginLeft: '0.15rem' }} />
                </button>

            </div>

            {/* ── Repair Quote Modal ── */}
            <RepairQuoteModal
                visible={quoteModal}
                onHide={() => setQuoteModal(false)}
                item={item}
                invoiceFile={invoiceFile}
            />
        </div>
    );
};
